import { useEffect, useState } from "react";
import { Reveal } from "@/hooks/useReveal";
import { Play, Instagram, X } from "lucide-react";
import { api, resolveImage } from "@/lib/api";

// Fallback videos shown when admin hasn't added any yet.
const FALLBACK_VIDEOS = [
  {
    id: "v1",
    title: "Inside a $42M Bel Air Estate",
    handle: "@manzanohomes",
    views: "1.2M",
    poster_url: "https://images.unsplash.com/photo-1613490493576-7fde63acd811?auto=format&fit=crop&w=600&q=80",
    video_url:
      "https://customer-assets.emergentagent.com/job_manzano-luxury/artifacts/mkpqdtkc_WhatsApp%20Video%202026-06-02%20at%2011.27.38%20AM.mp4",
  },
  {
    id: "v2",
    title: "Malibu Cliffside Tour",
    handle: "@manzanohomes",
    views: "874K",
    poster_url: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=600&q=80",
    video_url: "",
  },
  {
    id: "v3",
    title: "Trousdale at Twilight",
    handle: "@manzanohomes",
    views: "510K",
    poster_url: "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?auto=format&fit=crop&w=600&q=80",
    video_url: "",
  },
  {
    id: "v4",
    title: "Pacific Coast Compound",
    handle: "@manzanohomes",
    views: "2.1M",
    poster_url: "https://images.unsplash.com/photo-1512917774080-9991f1c4c750?auto=format&fit=crop&w=600&q=80",
    video_url: "",
  },
  {
    id: "v5",
    title: "Architectural Holmby",
    handle: "@manzanohomes",
    views: "318K",
    poster_url: "https://images.unsplash.com/photo-1564013799919-ab600027ffc6?auto=format&fit=crop&w=600&q=80",
    video_url: "",
  },
  {
    id: "v6",
    title: "Hidden Hills Modern",
    handle: "@manzanohomes",
    views: "627K",
    poster_url: "https://images.unsplash.com/photo-1600047509807-ba8f99d2cdde?auto=format&fit=crop&w=600&q=80",
    video_url: "",
  },
];

const VideoLightbox = ({ video, onClose }) => {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  return (
    <div
      data-testid="video-lightbox"
      onClick={onClose}
      className="fixed inset-0 z-[200] bg-black/95 backdrop-blur-sm flex items-center justify-center p-4 md:p-10 animate-in fade-in"
    >
      <button
        data-testid="video-lightbox-close"
        onClick={onClose}
        aria-label="Close video"
        className="absolute top-6 right-6 md:top-10 md:right-10 text-white/80 hover:text-white transition-colors"
      >
        <X size={28} strokeWidth={1.5} />
      </button>

      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-[480px] aspect-[9/16] bg-black"
      >
        <video
          data-testid="video-lightbox-player"
          src={resolveImage(video.video_url)}
          controls
          autoPlay
          playsInline
          className="w-full h-full object-contain"
        />
      </div>

      <div className="absolute bottom-6 left-0 right-0 text-center text-white/70 px-6">
        <p className="font-heading text-lg md:text-xl">{video.title}</p>
        <p className="text-[10px] tracking-[0.32em] uppercase mt-2 opacity-60">
          {video.handle}{video.views ? ` · ${video.views}` : ""}
        </p>
      </div>
    </div>
  );
};

const SocialVideos = () => {
  const [active, setActive] = useState(null);
  const [videos, setVideos] = useState(FALLBACK_VIDEOS);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/social-videos", { params: { featured: true } });
        if (Array.isArray(data) && data.length > 0) {
          setVideos(data);
        }
      } catch {
        // keep fallback silently
      }
    })();
  }, []);

  const handleClick = (v) => {
    if (v.video_url) {
      setActive(v);
    }
  };

  return (
    <section
      id="social"
      data-testid="social-videos-section"
      className="relative bg-white py-28 md:py-40 px-6 md:px-12"
    >
      <div className="max-w-[1400px] mx-auto">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16 md:mb-24">
          <div>
            <Reveal>
              <p className="overline mb-6">Coaching &amp; Closings</p>
            </Reveal>
            <Reveal delay={120}>
              <h2 className="font-heading text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-black">
                The lessons.
                <br />
                <em className="italic font-light">The listings.</em>
              </h2>
            </Reveal>
          </div>
          <Reveal delay={200}>
            <a
              href="https://www.instagram.com/manzanohomes/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="follow-instagram-link"
              className="inline-flex items-center gap-3 text-[11px] uppercase tracking-[0.28em] text-black hover:opacity-60 transition-opacity"
            >
              <Instagram size={16} />
              Follow @manzanohomes
            </a>
          </Reveal>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
          {videos.map((v, i) => (
            <Reveal key={v.id} delay={i * 60}>
              <button
                type="button"
                onClick={() => handleClick(v)}
                data-testid={`social-video-${v.id}`}
                className={`group relative block w-full aspect-[9/16] overflow-hidden bg-[#0a0a0a] text-left ${
                  v.video_url ? "cursor-pointer" : "cursor-default"
                }`}
              >
                {v.video_url ? (
                  <video
                    src={`${resolveImage(v.video_url)}#t=0.5`}
                    poster={v.poster_url ? resolveImage(v.poster_url) : undefined}
                    preload="metadata"
                    muted
                    playsInline
                    className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:scale-105 transition-transform duration-[900ms]"
                  />
                ) : (
                  <img
                    src={v.poster_url ? resolveImage(v.poster_url) : ""}
                    alt={v.title}
                    loading="lazy"
                    decoding="async"
                    referrerPolicy="no-referrer"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                    className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:scale-105 transition-transform duration-[900ms]"
                  />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-black/40" />
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-14 h-14 rounded-full border border-white/70 flex items-center justify-center backdrop-blur-sm bg-black/20 group-hover:scale-110 transition-transform duration-500">
                  <Play size={18} className="text-white translate-x-[1px]" fill="white" />
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                  <p className="text-xs font-medium leading-snug line-clamp-2">{v.title}</p>
                  <div className="flex items-center justify-between mt-2 text-[10px] tracking-[0.2em] uppercase opacity-70">
                    <span>{v.handle}</span>
                    <span>{v.views}</span>
                  </div>
                </div>
              </button>
            </Reveal>
          ))}
        </div>
      </div>

      {active && <VideoLightbox video={active} onClose={() => setActive(null)} />}
    </section>
  );
};

export default SocialVideos;
